/**
 * Main-thread side of the cloud fluid worker.
 *
 * Accumulates simulated time and asks the worker for a step at most every
 * MIN_STEP_INTERVAL_MS of wall time, and only when the previous step has
 * come back. The density comes back as bytes; it is copied into the
 * DataTexture's own array and the buffer goes back with the next request.
 * Stable fluids is unconditionally stable, so a large accumulated dt per
 * step is fine (capped at MAX_STEP_S to keep advection reasonably accurate).
 */
import { DataTexture, LinearFilter, RedFormat, UnsignedByteType } from 'three/webgpu';
import type { CloudLayerParams } from '../physics/cloudFluid';
import type { FluidRequest, FluidResponse } from '../physics/fluidProtocol';

const MIN_STEP_INTERVAL_MS = 100; // ~10 Hz
const MAX_STEP_S = 180;

export interface FluidClient {
  /** Current density texture (replaced when the grid size changes). */
  readonly texture: DataTexture;
  readonly n: number;
  /** Worker time for the last step, ms. */
  readonly lastStepMs: number;
  /** (Re)build the grid. Not hot path. */
  init(params: CloudLayerParams): void;
  /** Refill forcing after slider changes, keeping the fluid state. */
  updateParams(params: CloudLayerParams): void;
  /** Call every physics step with simulated dt. Allocation-free except the step message. */
  tick(simDtS: number, nowMs: number): void;
  dispose(): void;
}

function makeTexture(n: number): DataTexture {
  const tex = new DataTexture(new Uint8Array(n * n), n, n, RedFormat, UnsignedByteType);
  tex.magFilter = LinearFilter;
  tex.minFilter = LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

export function createFluidClient(): FluidClient {
  const worker = new Worker(new URL('../physics/fluidWorker.ts', import.meta.url), {
    type: 'module',
  });
  let n = 0;
  let texture = makeTexture(1);
  let buffer: ArrayBuffer | null = null;
  let busy = false;
  let pendingS = 0;
  let lastSendMs = -Infinity;
  let lastStepMs = 0;

  function send(msg: FluidRequest, transfer: Transferable[] = []): void {
    worker.postMessage(msg, transfer);
  }

  worker.onmessage = (e: MessageEvent<FluidResponse>): void => {
    const msg = e.data;
    busy = false;
    lastStepMs = msg.ms;
    if (msg.n === n && msg.buffer.byteLength === n * n) {
      (texture.image.data as Uint8Array).set(new Uint8Array(msg.buffer));
      texture.needsUpdate = true;
      buffer = msg.buffer;
    } else {
      // Grid changed while this step was in flight; size a new buffer.
      buffer = new ArrayBuffer(n * n);
    }
  };

  return {
    get texture(): DataTexture {
      return texture;
    },
    get n(): number {
      return n;
    },
    get lastStepMs(): number {
      return lastStepMs;
    },
    init(params: CloudLayerParams): void {
      if (params.n !== n) {
        n = params.n;
        texture.dispose();
        texture = makeTexture(n);
        if (!busy) buffer = new ArrayBuffer(n * n);
      }
      pendingS = 0;
      send({ type: 'init', params });
    },
    updateParams(params: CloudLayerParams): void {
      send({ type: 'params', params });
    },
    tick(simDtS: number, nowMs: number): void {
      pendingS += simDtS;
      if (busy || buffer === null || nowMs - lastSendMs < MIN_STEP_INTERVAL_MS) return;
      const dt = pendingS < MAX_STEP_S ? pendingS : MAX_STEP_S;
      pendingS = 0;
      lastSendMs = nowMs;
      busy = true;
      const b = buffer;
      buffer = null;
      send({ type: 'step', dtS: dt, buffer: b }, [b]);
    },
    dispose(): void {
      worker.terminate();
      texture.dispose();
    },
  };
}
